import React, { useEffect, useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { FeatureKey } from '@/types/subscription';
import { UpgradeBanner } from './UpgradeBanner';

interface FeatureGuardProps {
  feature: FeatureKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showBanner?: boolean;
  requireLimitCheck?: boolean; // If true, checks if limit is reached (for creation actions)
  currentUsage?: number;
  requiredTier?: 'basic' | 'advanced' | 'enterprise'; // For tier-based features
}

export const FeatureGuard: React.FC<FeatureGuardProps> = ({ 
  feature, 
  children, 
  fallback, 
  showBanner = false,
  requireLimitCheck = false,
  currentUsage,
  requiredTier
}) => {
  const { canAccess, isLoading, subscription } = useSubscription();
  const [allowed, setAllowed] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(true);

  useEffect(() => {
    const check = async () => {
      if (isLoading) return;
      
      // If we need to check limits (e.g. "Can I create another branch?"), we pass currentUsage
      // If we just check access (e.g. "Can I see the inventory module?"), we don't pass usage
      const usageToCheck = requireLimitCheck ? (currentUsage ?? 0) : undefined;
      
      const isAllowed = await canAccess(feature, usageToCheck);
      
      // Additional check for tier-based features
      if (isAllowed && requiredTier && subscription) {
        const featureValue = subscription.features[feature];
        
        if (typeof featureValue === 'string') {
          const tierLevels = {
            basic: 1,
            advanced: 2,
            enterprise: 3,
          };
          
          const currentLevel = tierLevels[featureValue as keyof typeof tierLevels] || 0;
          const requiredLevel = tierLevels[requiredTier];
          
          setAllowed(currentLevel >= requiredLevel);
          setChecking(false);
          return;
        }
      }
      
      setAllowed(isAllowed);
      setChecking(false);
    };

    check();
  }, [feature, canAccess, isLoading, requireLimitCheck, currentUsage, requiredTier, subscription]);

  if (isLoading || checking) {
    return null; // Or a skeleton
  }

  if (!allowed) {
    if (fallback) return <>{fallback}</>;
    if (showBanner) return <UpgradeBanner />;
    return null;
  }

  return <>{children}</>;
};
